import React, { createRef, useState } from 'react';

export interface ISearcherOptions {
    onSearch: (search: string) => Promise<void>;
    placeholder: string;
    searchAriaLabel: string;
    clearAriaLabel: string;
    debounce?: number;
}

export interface ISearcher {
    input: React.InputHTMLAttributes<HTMLInputElement>;
    searchButton: React.ButtonHTMLAttributes<HTMLButtonElement>;
    cancelButton: React.ButtonHTMLAttributes<HTMLButtonElement>;
    ref: React.RefObject<HTMLDivElement>;
    hasSearch: boolean;
}

export const useSearch = ({ onSearch, placeholder, searchAriaLabel, clearAriaLabel, debounce = 500 }: ISearcherOptions): ISearcher => {
    const searchTimeout = React.useRef<number>(0);
    const [searchString, setSearchString] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const ref = createRef<HTMLDivElement>();
    const prevSearchString = React.useRef<string>('');

    React.useEffect(() => {
        return () => window.clearTimeout(searchTimeout.current);
    }, []);

    React.useEffect(() => {
        const element = ref.current;

        if (element) {
            const handleKeyboardEvent = (ev: KeyboardEvent) => {
                if (ev.code === 'Escape') {
                    ev.stopPropagation();
                    ev.preventDefault();
                    setSearchString('');
                    triggerSearchEvent('');
                } else if (ev.code === 'Enter') {
                    ev.preventDefault();
                    triggerSearchEvent(searchString);
                }
            };

            element.addEventListener('keydown', handleKeyboardEvent);

            return () => element.removeEventListener('keydown', handleKeyboardEvent);
        }
    }, [ref]);

    const triggerSearchEvent = (search: string) => {
        if (prevSearchString.current === search) {
            return;
        }
        if (!isSearching) {
            setIsSearching(true);
            onSearch(search).finally(() => {
                setIsSearching(false);
                prevSearchString.current = search;
            });
        }
    };

    const handleInputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;

        window.clearTimeout(searchTimeout.current);
        setSearchString(val);

        if (val.length >= 3) {
            searchTimeout.current = window.setTimeout(() => {
                window.clearTimeout(searchTimeout.current);
                triggerSearchEvent(val);
            }, debounce);
        }

        if (val.length === 0) {
            searchTimeout.current = window.setTimeout(() => {
                window.clearTimeout(searchTimeout.current);
                triggerSearchEvent('');
            }, debounce);
        }
    };

    return {
        ref,
        hasSearch: searchString.length > 0,
        input: {
            onChange: handleInputOnChange,
            value: searchString,
            type: 'search',
            'aria-label': searchAriaLabel,
            placeholder: placeholder,
        },
        searchButton: {
            onClick: () => triggerSearchEvent(searchString),
            title: searchAriaLabel,
            'aria-label': searchAriaLabel,
            type: 'button',
        },
        cancelButton: {
            onClick: () => {
                setSearchString('');
                triggerSearchEvent('');
            },
            title: clearAriaLabel,
            'aria-label': clearAriaLabel,
            type: 'button',
            style: { visibility: searchString.length > 0 ? 'visible' : 'hidden' },
        },
    };
};
